import type { ReactNode } from "react"
import Select from "@/v2-ui/select"
import { useLicensePlansQuery } from "@/v2-backoffice-client/license/plan/license.plan.query"

export type LicensePlanTargetSelectProps = {
  value: string
  onValueChange(value: string): void
  /** The license being migrated from / deprecated — never a legal target. */
  excludePlanId?: number
  placeholder?: string
  /** Extra leading items (e.g. a "no target yet" sentinel). */
  children?: ReactNode
}

// A migration-target picker: every license that customers could legally move TO
// — deprecated licenses and the source license itself are filtered out (the proc
// refuses them; hiding them beats surfacing the error). Owns the licenses query
// (cache-shared with the board), keeping the dialogs one-operation
// components. Used for the license's suggested successor and the per-customer
// agreed target.
function LicensePlanTargetSelect(props: LicensePlanTargetSelectProps) {
  const {
    value,
    onValueChange,
    excludePlanId,
    placeholder = "Pick a license...",
    children,
  } = props

  const { data, loading } = useLicensePlansQuery()
  const plans = (data?.getLicensePlans ?? []).filter(
    (plan) => !plan.DeprecatedAt && plan.Id !== excludePlanId,
  )

  return (
    <Select value={value} onValueChange={onValueChange}>
      <Select.Trigger size="md" className="w-full">
        <Select.Value
          placeholder={loading ? "Loading licenses..." : placeholder}
        />
      </Select.Trigger>
      <Select.Content>
        {children}
        {plans.map((plan) => (
          <Select.Item key={plan.Id} value={String(plan.Id)}>
            {plan.Name}
            {plan.ProductName ? ` — ${plan.ProductName}` : ""}
          </Select.Item>
        ))}
      </Select.Content>
    </Select>
  )
}

export default LicensePlanTargetSelect
